/**
 * TeacherStudentStatePanel — page at /teacher/state in school-ui. Lets
 * an admin/teacher select a (student, agent) and observe the live
 * shared state for that triple via AG-UI.
 *
 * Uses CopilotKit's React SDK against services/copilot-runtime/, which
 * proxies AG-UI streams to Nova OS's /agents/v1 endpoints. The sidecar
 * reads X-Platform-User + X-End-User from the inbound request and
 * stamps them on the outbound HttpAgent call, scoping the
 * persist_fields state to the (teacher, student, agent) triple.
 *
 * **Provider constraint (deliberate — Phase 2 design decision):** this
 * page owns its own <CopilotKit> Provider so `headers` can be keyed
 * per selected student. Phase 2 deliberately keeps the Provider here
 * rather than lifting it to app root because app-root headers can't
 * vary per student. Phase 3's chat page will own its own <CopilotKit>
 * separately — no nesting because routes are siblings, never nested.
 *
 * Migrated from admin-ui/TeacherStudentStatePanel.tsx as part of #7
 * (Phase 2 of #5).
 */
import { useMemo, useState } from "react";
import { CopilotKit, useAgent, useCopilotKit } from "@copilotkit/react-core/v2";
import { createClientUuid } from "@/clientId";

// ────────────────────────────────────────────────────────────────────────────
// Public component
// ────────────────────────────────────────────────────────────────────────────
interface TeacherStudentStatePanelProps {
  /** Teacher's Nova OS user id — forwarded as X-Platform-User. */
  teacherId: string;
  /** Students this teacher has scope to observe. */
  students: Array<{ id: string; displayName: string }>;
  /** Agents available in this panel. Defaults to the three subject agents
   *  this repo ships templates for. Must match the AGENT_IDS configured
   *  on the copilot-runtime sidecar. */
  agents?: Array<{ id: string; displayName: string }>;
  /** Sidecar endpoint. Defaults to /api/copilotkit. */
  runtimeUrl?: string;
}

const DEFAULT_AGENTS = [
  { id: "chemistry-assistant", displayName: "Chemistry" },
  { id: "math-assistant", displayName: "Math" },
  { id: "english-assistant", displayName: "English" },
];

const DEFAULT_PROBE =
  "Summarize what this student currently knows and is working on. Keep it under 5 bullet points.";

export function TeacherStudentStatePanel(props: TeacherStudentStatePanelProps) {
  const { teacherId, students, agents = DEFAULT_AGENTS, runtimeUrl = "/api/copilotkit" } = props;
  const [studentId, setStudentId] = useState(students[0]?.id ?? "");
  const [agentId, setAgentId] = useState(agents[0]?.id ?? "");

  return (
    <div className="flex h-full">
      <aside className="w-64 border-r border-slate-200 p-4 overflow-y-auto">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Students</h2>
        <ul className="space-y-1">
          {students.map((s) => (
            <li key={s.id}>
              <button
                onClick={() => setStudentId(s.id)}
                className={`w-full text-left p-2 rounded text-sm ${
                  studentId === s.id ? "bg-blue-50 text-blue-600" : "hover:bg-slate-100"
                }`}
              >
                {s.displayName}
              </button>
            </li>
          ))}
          {students.length === 0 && (
            <li className="text-slate-500 text-xs italic">No students in scope.</li>
          )}
        </ul>
      </aside>

      <main className="flex-1 overflow-y-auto p-6">
        <header className="mb-4 flex items-center gap-3">
          <h1 className="text-lg font-semibold">Live student state</h1>
          <select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            className="px-2 py-1 text-sm rounded bg-slate-100 border border-slate-200"
          >
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.displayName}
              </option>
            ))}
          </select>
          <span className="text-xs text-slate-500">
            scope:{" "}
            <code className="text-blue-600">
              ({teacherId.slice(0, 8)}, {studentId.slice(0, 8) || "—"}, {agentId})
            </code>
          </span>
        </header>

        {/* Re-mount the entire CopilotKit subtree when scope changes so the
            agent state cache resets to whatever Nova OS streams for the
            new (teacher, student, agent) triple. */}
        {studentId ? (
          <CopilotKit
            key={`${teacherId}-${studentId}`}
            runtimeUrl={runtimeUrl}
            // copilot-runtime is multi-route; the Provider's default
            // is single-endpoint. Opt into REST transport so paths
            // match (/info, /agent/<id>/run).
            useSingleEndpoint={false}
            headers={{
              "X-Platform-User": teacherId,
              "X-End-User": studentId,
            }}
          >
            <ScopedAgentView agentId={agentId} />
          </CopilotKit>
        ) : (
          <p className="text-slate-500 text-sm italic">Select a student to begin observing.</p>
        )}
      </main>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Inner view — reads from useAgent. Lives under the CopilotKit Provider.
// ────────────────────────────────────────────────────────────────────────────
function ScopedAgentView({ agentId }: { agentId: string }) {
  const { agent } = useAgent({ agentId });
  const { copilotkit } = useCopilotKit();
  const [probeText, setProbeText] = useState(DEFAULT_PROBE);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const isRunning = agent?.isRunning ?? false;
  const state = (agent?.state as Record<string, unknown> | undefined) ?? {};
  const messages = useMemo(() => agent?.messages ?? [], [agent?.messages]);

  const runProbe = async () => {
    if (!agent || !probeText.trim()) return;
    setErrMsg(null);
    try {
      agent.addMessage({
        id: createClientUuid(),
        role: "user",
        content: probeText,
      });
      await copilotkit.runAgent({ agent });
    } catch (e) {
      setErrMsg((e as Error).message);
    }
  };

  return (
    <>
      <section className="mb-6 rounded border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold mb-2">Persisted state (live)</h3>
        {Object.keys(state).length === 0 ? (
          <p className="text-slate-500 text-xs italic">
            No state yet. Run a probe to start a stream — STATE_DELTA events from the agent will populate
            this panel as it works.
          </p>
        ) : (
          <pre className="text-xs bg-slate-100 rounded p-2 overflow-x-auto">
            {JSON.stringify(state, null, 2)}
          </pre>
        )}
      </section>

      <section className="mb-6">
        <h3 className="text-sm font-semibold mb-2">Probe</h3>
        <textarea
          value={probeText}
          onChange={(e) => setProbeText(e.target.value)}
          rows={2}
          className="w-full px-2 py-1 text-sm rounded bg-slate-100 border border-slate-200"
          placeholder="Ask the agent something whose answer drains the student's persisted state…"
        />
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={runProbe}
            disabled={!agent || !probeText.trim() || isRunning}
            className="text-xs px-3 py-1 rounded bg-blue-100 text-blue-600 disabled:opacity-50"
          >
            {isRunning ? "Streaming…" : "Run probe"}
          </button>
          {isRunning && (
            <span className="text-[10px] uppercase tracking-wide bg-blue-100 text-blue-600 px-2 py-0.5 rounded animate-pulse">
              streaming
            </span>
          )}
          {errMsg && <span className="text-xs text-red-600">{errMsg}</span>}
        </div>
      </section>

      {messages.length > 0 && (
        <section className="rounded border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold mb-2">Conversation</h3>
          <ul className="space-y-2">
            {messages
              .filter((m) => (m.role === "user" || m.role === "assistant") && typeof (m as { content?: unknown }).content === "string")
              .map((m, i) => (
                <li key={m.id ?? i} className="text-sm">
                  <span className="text-slate-500 text-[10px] uppercase tracking-wide mr-2">{m.role}</span>
                  <span className="whitespace-pre-wrap">{(m as { content: string }).content}</span>
                </li>
              ))}
          </ul>
        </section>
      )}
    </>
  );
}
